import React, {
  Fragment,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  // useDispatch,
  useSelector,
} from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  checksheetCopy,
  // checksheetGetAllByYear,
} from 'apis/checksheetAPIs';
// import { setChecksheetList } from 'state/slices/dataListsSlice';
import { Button } from '@asu/components-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuildingColumns,
  faLocationDot,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import StaticData from './StaticData';
import { classNames } from 'utils';
import useServiceAuth from 'auth/useServiceAuth';

const proposedString = '(proposed) ';

const SearchProgram = ({ program }) => {
  const { degree, owners, campusesOffered } = useMemo(() => {
    const degree = program.degreeDescriptionShort
      ? program.degreeDescriptionShort === 'CERT'
        ? ', Certificate'
        : `, ${program.degreeDescriptionShort}`
      : program.acadPlanType === 'MIN'
        ? ', Minor'
        : null;

    // Use Set for O(1) deduplication
    const ownerSet = new Set();
    const owners = [];
    program.owners.forEach((programOwner) => {
      if (!ownerSet.has(programOwner.collegeAcadOrg)) {
        ownerSet.add(programOwner.collegeAcadOrg);
        owners.push({
          collegeAcadOrg: programOwner.collegeAcadOrg,
          collegeDescription: programOwner.collegeDescription,
        });
      }
    });

    const campusesOffered = [];
    if (program.campusesOffered && program.campusesOffered.length > 0) {
      program.campusesOffered.forEach((campus) => {
        const obj = { ...campus };
        const campusFound = StaticData.campusUndergradList.find(
          (campusUndergrad) => campusUndergrad.value === campus.campusCode
        );
        if (campusFound?.label) obj.label = campusFound.label;
        campusesOffered.push(obj);
      });
    }

    return { degree, owners, campusesOffered };
  }, [program]);

  return (
    <>
      <div className="d-flex gap-2 justify-content-between">
        <div className="fw-bold" style={{ fontSize: '18px' }}>
          {program.acadPlanDescription}
          {degree}
        </div>
        <div style={{ marginTop: '3px' }}>{program.acadPlanCode}</div>
      </div>
      {program.subplan && (
        // program.acadPlanCode !== 'LSBISBIS' &&
        <div className="d-flex gap-2 justify-content-between">
          <div className="fw-bold">
            {program.subplan.description}
            <span className="subplan-tag ms-1">Subplan</span>
          </div>
          <div style={{ marginTop: '3px' }}>
            {program.subplan.acadSubPlanCode}
          </div>
        </div>
      )}
      <div className="d-flex gap-1 align-items-center">
        <FontAwesomeIcon
          icon={faBuildingColumns}
          className="text-gray-6"
          width={'16px'}
        />
        {owners.map((owner, index, array) => (
          <Fragment key={owner.collegeAcadOrg}>
            {owner.collegeDescription}
            {index !== array.length - 1 && ', '}
          </Fragment>
        ))}
      </div>
      <div className="d-flex gap-1 align-items-center">
        <FontAwesomeIcon
          icon={faLocationDot}
          className="text-gray-6"
          width={'16px'}
        />
        {campusesOffered.map((campus, index, array) => (
          <Fragment key={campus.campusCode}>
            {campus.label}
            {index !== array.length - 1 && ', '}
          </Fragment>
        ))}
      </div>
    </>
  );
};

const NewChecksheet = ({
  checksheet,
  programList,
  refetchChecksheets,
  handleClose,
}) => {
  const { getAccessToken } = useServiceAuth();
  const navigate = useNavigate();

  const [searchString, setSearchString] = useState('');
  const [currentChecksheetDegree, setCurrentChecksheetDegree] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedProgramDegree, setSelectedProgramDegree] = useState(null);
  const [selectedChecksheet, setSelectedChecksheet] = useState(null);
  const year = useSelector((state) => state.settings.year);
  const [selectedRadio, setSelectedRadio] = useState(null);

  const checksheetList = useSelector((state) => state.dataLists.checksheetList);
  // const idsConcentrations = useSelector(
  //   (state) => state.dataLists.idsConcentrationList
  // );
  const proposedList = useSelector(
    (state) => state.dataLists.proposedChecksheetList
  );
  const collegeList = useSelector((state) => state.dataLists.collegeList);
  const isModalOpen = useSelector((state) => state.modal.isOpen);

  // Create lookup maps for O(1) access
  const checksheetLookup = useMemo(() => {
    const lookup = new Map();
    checksheetList.forEach((checksheet) => {
      const [, , planCode, subPlanCode] = checksheet.id.split('*');
      const key = `${planCode}:${subPlanCode}`;
      lookup.set(key, checksheet);
    });
    return lookup;
  }, [checksheetList]);

  // Optimize idsChecksheets computation
  // const idsChecksheets = useMemo(() => {
  //   const checksheets = {};
  //   const checksheetMap = new Map();

  //   // Build lookup map first
  //   checksheetList.forEach((cs) => {
  //     const [, college, planCode, subPlanCode] = cs.id.split('*');
  //     checksheetMap.set(`${college}:${planCode}:${subPlanCode}`, cs);
  //   });

  //   // Single pass through concentrations
  //   idsConcentrations
  //     .filter((conc) => conc.owner)
  //     .forEach((conc) => {
  //       const key = `${conc.owner.college}:LSBISBIS:${conc.subplan.acadSubPlanCode}`;
  //       const checksheet = checksheetMap.get(key);
  //       if (checksheet) {
  //         checksheets[conc.subplan.acadSubPlanCode] = checksheet;
  //       }
  //     });

  //   return checksheets;
  // }, [checksheetList, idsConcentrations]);

  // Optimize search filtering with single pass
  const filteredResults = useMemo(() => {
    if (!searchString) return { programs: [], proposed: [], ids: [] };

    const searchLower = searchString.toLowerCase();

    // Filter programs with single pass
    const programs = programList.filter((program) => {
      const key = `${program.acadPlanCode}:${program.subplan?.acadSubPlanCode || 'null'}`;
      if (!checksheetLookup.has(key)) return false;

      return (
        program.acadPlanCode.toLowerCase().includes(searchLower) ||
        program.acadPlanDescription.toLowerCase().includes(searchLower) ||
        (program.subplan &&
          (program.subplan.acadSubPlanCode
            .toLowerCase()
            .includes(searchLower) ||
            program.subplan.description.toLowerCase().includes(searchLower)))
      );
    });

    // Filter proposed with single pass
    const proposed = proposedList
      .filter(
        (p) =>
          p.checksheetObj.acadplan.toLowerCase().includes(searchLower) ||
          (proposedString + p.checksheetName)
            .toLowerCase()
            .includes(searchLower)
      )
      .sort((a, b) => a.checksheetName.localeCompare(b.checksheetName));

    // Filter IDS concentrations with single pass
    // const ids = idsConcentrations.filter(
    //   (conc) =>
    //     conc.owner &&
    //     idsChecksheets[conc.subplan.acadSubPlanCode] &&
    //     (conc.acadPlanCode.toLowerCase().includes(searchLower) ||
    //       conc.acadPlanDescription.toLowerCase().includes(searchLower) ||
    //       conc.subplan.acadSubPlanCode.toLowerCase().includes(searchLower))
    // );

    return {
      programs,
      proposed,
      // ids
    };
  }, [
    searchString,
    programList,
    checksheetLookup,
    proposedList,
    // idsConcentrations,
    // idsChecksheets,
  ]);

  // Save new checksheet to database
  const { mutate: copyChecksheet } = useMutation({
    mutationFn: checksheetCopy,
    onSuccess: async (data, { token }) => {
      refetchChecksheets();
    },
  });

  const handleCreateChecksheet = async () => {
    if (selectedRadio === 'blankChecksheet') {
      navigate(
        `/checksheet?id=${year}*${checksheet.collegeCode}*${checksheet.program.acadPlanCode}*${!!checksheet.program.subplan ? checksheet.program.subplan.acadSubPlanCode : 'null'}`
      );
      handleClose();
    } else if (selectedChecksheet) {
      const jsonData = {
        checksheetfrom: selectedChecksheet.id,
        acadplanto: checksheet.program.acadPlanCode,
        acadsubplanto: checksheet.program.subplan
          ? checksheet.program.subplan.acadSubPlanCode
          : 'null',
        collegeto: checksheet.collegeCode,
        departmentto: checksheet.departmentCode,
        nameto:
          checksheet.program.acadPlanDescription + currentChecksheetDegree,
        programtypeto:
          // checksheet.program.acadPlanCode === 'LSBISBIS' &&
          // checksheet.program.subplan
          //   ? 'IDS'
          //   :
          checksheet.program.degreeDescriptionShort ??
          checksheet.program.acadPlanType,
        yearto: year,
      };

      console.log('JSON: ', jsonData);

      const token = await getAccessToken();
      copyChecksheet({ jsonData: jsonData, token: token });

      handleClose();
    } else {
      alert('Program not selected');
    }
  };

  const handleSelectRadio = (e) => {
    setSelectedRadio(e.target.value);
  };

  const handleSelectChecksheet = useCallback(
    (program) => {
      const key = `${program.acadPlanCode}:${program.subplan?.acadSubPlanCode || 'null'}`;
      const cs = checksheetLookup.get(key);
      setSelectedProgram(program);
      setSelectedChecksheet(cs);
    },
    [checksheetLookup]
  );

  const handleSelectProposed = (proposed) => {
    setSelectedProgram('PROPOSED');
    setSelectedChecksheet(proposed);
  };

  // const handleSelectIdsChecksheet = (concentration, checksheet) => {
  //   setSelectedProgram(concentration);
  //   setSelectedChecksheet(checksheet);
  // };

  const handleClearSelectedChecksheet = () => {
    setSearchString('');
    setSelectedChecksheet(null);
  };

  useEffect(() => {
    if (checksheet && checksheet.program) {
      const dds = checksheet.program.degreeDescriptionShort
        ? checksheet.program.degreeDescriptionShort === 'CERT'
          ? ', Certificate'
          : `, ${checksheet.program.degreeDescriptionShort}`
        : checksheet.program.acadPlanType === 'MIN'
          ? ', Minor'
          : null;

      setCurrentChecksheetDegree(dds);
    }
  }, [checksheet]);

  useEffect(() => {
    if (selectedProgram) {
      const dds = selectedProgram.degreeDescriptionShort
        ? selectedProgram.degreeDescriptionShort === 'CERT'
          ? ', Certificate'
          : `, ${selectedProgram.degreeDescriptionShort}`
        : selectedProgram.acadPlanType === 'MIN'
          ? ', Minor'
          : null;

      setSelectedProgramDegree(dds);
    }
  }, [selectedProgram]);

  useEffect(() => {
    if (!isModalOpen) {
      setSelectedRadio(null);
      setSearchString('');
      setSelectedChecksheet(null);
    }
  }, [isModalOpen]);

  if (!checksheet || !checksheet.program) return <div>Loading...</div>;

  return (
    <div className="p-6" style={{ width: '832px' }}>
      <div className="fs-4 fw-bold">
        {checksheet.program.acadPlanDescription}
        {currentChecksheetDegree}
      </div>
      <div className="fs-1 fw-bold mb-4">New checksheet</div>

      <form className="uds-form">
        <fieldset>
          <div className="form-check">
            <input
              className="form-check-input"
              type="radio"
              id="blankChecksheet"
              data-ga-input="radio button"
              data-ga-input-name="onclick"
              data-ga-input-event="select"
              data-ga-input-action="click"
              data-ga-input-region="main content"
              data-ga-input-section="Create from blank checksheet"
              value="blankChecksheet"
              checked={selectedRadio === 'blankChecksheet'}
              onChange={(e) => handleSelectRadio(e)}
            />
            <label className="form-check-label" htmlFor="blankChecksheet">
              <div>Create from blank checksheet</div>
              <div className="fw-normal text-gray-6">
                Build program requirements from scratch
              </div>
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="radio"
              id="existingProgram"
              data-ga-input="radio button"
              data-ga-input-name="onclick"
              data-ga-input-event="select"
              data-ga-input-action="click"
              data-ga-input-region="main content"
              data-ga-input-section="Create from existing program"
              value="existingChecksheet"
              checked={selectedRadio === 'existingChecksheet'}
              onChange={(e) => handleSelectRadio(e)}
            />
            <label className="form-check-label" htmlFor="existingProgram">
              <div>Create from existing checksheet</div>
              <div className="fw-normal text-gray-6">
                Start with an existing checksheet and make modifications as
                needed. Recommended only for similar programs with shared
                requirements.
              </div>
            </label>
          </div>
        </fieldset>
      </form>

      {selectedRadio === 'existingChecksheet' && (
        <div className="mb-9">
          {!selectedChecksheet ? (
            <>
              <div className="fw-bold lh-1 mb-1">Search programs</div>
              <div className="position-relative">
                <input
                  placeholder="Search by program title or plan code"
                  value={searchString}
                  onChange={(e) => setSearchString(e.target.value)}
                  className="border border-gray-5 ps-2 pe-6 py-1 w-100"
                />
                {searchString && (
                  <div
                    className="position-absolute border border-top-0 border-gray-5 w-100 overflow-y-auto"
                    style={{ maxHeight: '198px', zIndex: 1 }}
                  >
                    {filteredResults.programs.map((program, index, array) => (
                      <div key={index} className="bg-white px-2">
                        <div
                          className={classNames(
                            index !== array.length - 1 ||
                              filteredResults.proposed.length > 0
                              ? // || filteredResults.ids.length > 0
                                'border-bottom'
                              : '',
                            'border-gray-5 py-2'
                          )}
                          onClick={() => handleSelectChecksheet(program)}
                          role="button"
                        >
                          <SearchProgram
                            program={program}
                            checksheet={checksheet}
                          />
                        </div>
                      </div>
                    ))}
                    {/* {filteredResults.ids.map((conc, index, array) => (
                      <div key={index} className="bg-white px-2">
                        <div
                          className={classNames(
                            index !== array.length - 1 ||
                              filteredResults.proposed.length > 0
                              ? 'border-bottom'
                              : '',
                            'border-gray-5 py-2'
                          )}
                          onClick={() =>
                            handleSelectIdsChecksheet(
                              conc,
                              idsChecksheets[conc.subplan.acadSubPlanCode]
                            )
                          }
                          role="button"
                        >
                          <div className="d-flex gap-2 justify-content-between">
                            <div
                              className="fw-bold"
                              style={{ fontSize: '18px' }}
                            >
                              {conc.acadPlanDescription}
                            </div>
                            <div style={{ marginTop: '3px' }}>
                              {conc.subplan.acadSubPlanCode}
                            </div>
                          </div>
                          <div className="fw-bold mb-1">
                            <span className="subplan-tag">
                              Interdisciplinary studies
                            </span>
                          </div>
                          <div className="d-flex gap-1 align-items-center">
                            <FontAwesomeIcon
                              icon={faBuildingColumns}
                              className="text-gray-6"
                              width={'16px'}
                            />
                            {conc?.owner?.college &&
                              !!collegeList?.[conc.owner.college] &&
                              collegeList[conc.owner.college].description}
                          </div>
                        </div>
                      </div>
                    ))} */}
                    {filteredResults.proposed.map((proposed, index, array) => (
                      <div key={index} className="bg-white px-2">
                        <div
                          className={classNames(
                            index !== array.length - 1 && 'border-bottom',
                            'border-gray-5 py-2'
                          )}
                          onClick={() => handleSelectProposed(proposed)}
                          role="button"
                        >
                          <div className="d-flex gap-2 justify-content-between">
                            <div
                              className="fw-bold"
                              style={{ fontSize: '18px' }}
                            >
                              (PROPOSED) {proposed.checksheetName}
                            </div>
                            <div style={{ marginTop: '3px' }}>
                              {proposed.checksheetObj.acadplan}
                            </div>
                          </div>
                          <div className="d-flex gap-1 align-items-center">
                            <FontAwesomeIcon
                              icon={faBuildingColumns}
                              className="text-gray-6"
                              width={'16px'}
                            />
                            {proposed?.checksheetObj?.college &&
                              (proposed.checksheetObj.college === 'OTHER'
                                ? 'Other'
                                : !!collegeList?.[
                                    proposed.checksheetObj.college
                                  ] &&
                                  collegeList[proposed.checksheetObj.college]
                                    .description)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : selectedProgram === 'PROPOSED' ? (
            <div className="bg-gray-1 border border-gray-3 w-100 mb-3">
              <div className="p-2">
                <div className="d-flex justify-content-between mb-1">
                  <div className="fw-bold" style={{ fontSize: '18px' }}>
                    {selectedChecksheet.checksheetName}
                  </div>
                  <div className="d-flex gap-3 align-items-center">
                    <div>{selectedChecksheet.checksheetObj.acadplan}</div>
                    <div
                      className="bg-white border border-gray-3 lh-1 rounded-circle"
                      style={{
                        height: 'min-content',
                        padding: '3px 6px 5px',
                      }}
                      onClick={handleClearSelectedChecksheet}
                      role="button"
                    >
                      <FontAwesomeIcon
                        icon={faXmark}
                        style={{ height: '12px', width: '12px' }}
                      />
                    </div>
                  </div>
                </div>
                <div className="d-flex gap-1 align-items-center mb-1">
                  <FontAwesomeIcon
                    icon={faBuildingColumns}
                    className="text-gray-5"
                    width={'16px'}
                  />
                  {selectedChecksheet?.checksheetObj?.college &&
                    (selectedChecksheet.checksheetObj.college === 'OTHER'
                      ? 'Other'
                      : !!collegeList?.[
                          selectedChecksheet.checksheetObj.college
                        ] &&
                        collegeList[selectedChecksheet.checksheetObj.college]
                          .description)}
                </div>
              </div>
            </div>
          ) : (
            // !!selectedProgram.owner ? (
            //   <div className="bg-gray-1 border border-gray-3 w-100 mb-3">
            //     <div className="p-2">
            //       <div className="d-flex justify-content-between">
            //         <div className="fw-bold" style={{ fontSize: '18px' }}>
            //           {selectedProgram.acadPlanDescription}
            //         </div>
            //         <div className="d-flex gap-3 align-items-center">
            //           <div>{selectedProgram.subplan.acadSubPlanCode}</div>
            //           <div
            //             className="bg-white border border-gray-3 lh-1 rounded-circle"
            //             style={{
            //               height: 'min-content',
            //               padding: '3px 6px 5px',
            //             }}
            //             onClick={handleClearSelectedChecksheet}
            //             role="button"
            //           >
            //             <FontAwesomeIcon
            //               icon={faXmark}
            //               style={{ height: '12px', width: '12px' }}
            //             />
            //           </div>
            //         </div>
            //       </div>
            //       <div className="fw-bold mb-1">
            //         <span className="subplan-tag">Interdisciplinary studies</span>
            //       </div>
            //       <div className="d-flex gap-1 align-items-center mb-1">
            //         <FontAwesomeIcon
            //           icon={faBuildingColumns}
            //           className="text-gray-5"
            //           width={'16px'}
            //         />
            //         {selectedProgram?.owner?.college &&
            //           !!collegeList?.[selectedProgram.owner.college] &&
            //           collegeList[selectedProgram.owner.college].description}
            //       </div>
            //     </div>
            //   </div>
            // ) :
            <>
              <div className="bg-gray-1 border border-gray-3 w-100 mb-3">
                <div className="p-2">
                  <div className="d-flex justify-content-between mb-1">
                    <div className="fw-bold" style={{ fontSize: '18px' }}>
                      {selectedProgram.acadPlanDescription}
                      {selectedProgramDegree}
                    </div>
                    <div className="d-flex gap-3 align-items-center">
                      <div>{selectedProgram.acadPlanCode}</div>
                      <div
                        className="bg-white border border-gray-3 lh-1 rounded-circle"
                        style={{
                          height: 'min-content',
                          padding: '3px 6px 5px',
                        }}
                        onClick={handleClearSelectedChecksheet}
                        role="button"
                      >
                        <FontAwesomeIcon
                          icon={faXmark}
                          style={{ height: '12px', width: '12px' }}
                        />
                      </div>
                    </div>
                  </div>

                  {selectedProgram.subplan && (
                    // selectedProgram !== 'LSBISBIS' &&
                    <div className="d-flex gap-2 justify-content-between">
                      <div className="fw-bold">
                        {selectedProgram.subplan.description}
                        <span className="subplan-tag ms-1">Subplan</span>
                      </div>
                      <div style={{ margin: '3px 50px 0 0' }}>
                        {selectedProgram.subplan.acadSubPlanCode}
                      </div>
                    </div>
                  )}

                  <div className="d-flex gap-1 align-items-center mb-1">
                    <FontAwesomeIcon
                      icon={faBuildingColumns}
                      className="text-gray-5"
                      width={'16px'}
                    />
                    {selectedProgram.owners.map((owner, index, array) => (
                      <Fragment key={owner.collegeAcadOrg}>
                        {owner.collegeDescription}
                        {index !== array.length - 1 && ', '}
                      </Fragment>
                    ))}
                  </div>
                  <div className="d-flex gap-1 align-items-center">
                    <FontAwesomeIcon
                      icon={faLocationDot}
                      className="text-gray-5"
                      width={'16px'}
                    />
                    {selectedProgram.campusesOffered.map(
                      (campus, index, array) => (
                        <Fragment key={campus.campusCode}>
                          {
                            StaticData.campusUndergradList.find(
                              (camp) => camp.value === campus.campusCode
                            ).label
                          }
                          {index !== array.length - 1 && ', '}
                        </Fragment>
                      )
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      <div className="d-flex justify-content-between align-items-center">
        <Button
          color="dark"
          label="Cancel"
          size="medium"
          onClick={handleClose}
        />
        <Button
          color="gold"
          label="Create checksheet"
          size="medium"
          onClick={handleCreateChecksheet}
          disabled={selectedRadio !== 'blankChecksheet' && !selectedChecksheet}
        />
      </div>
    </div>
  );
};

export default NewChecksheet;
